/**
 * String utility functions for common transformations and operations.
 *
 * A bit of history... This module (blegh... let's not be so fancy... file!) This file came about
 * when we were tracking a bunch of events or datapoints to send to our Amplitude analytics, where
 * they're more expected to be in snake_case.
 *
 * However, our backend API and most of our React code uses camelCase, and we want to be consistent
 * in our API calls, so we need to convert the data to snake_case when sending it to Amplitude.
 *
 * This file contains utility functions for converting strings between camelCase and snake_case.
 */

/**
 * Converts a camelCase string to snake_case.
 * Example: "thisIsATest" becomes "this_is_a_test"
 */
export const camelToSnakeCase = (str: string): string => {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)
}

/**
 * Converts a snake_case string to camelCase.
 * Example: "this_is_a_test" becomes "thisIsATest"
 */
export const snakeToCamelCase = (str: string): string => {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())
}

/**
 * Converts an object's keys from snake_case to camelCase, recursively.
 *
 * @param obj - The object to convert
 * @returns A new object with all keys in camelCase
 */
export const objectToCamelCase = <T>(
  obj: Record<string, unknown> | unknown[]
): T => {
  if (obj === null || typeof obj !== 'object') {
    return obj as T
  }

  if (Array.isArray(obj)) {
    return (obj.map((item) =>
      typeof item === 'object' && item !== null
        ? objectToCamelCase(item as Record<string, unknown>)
        : item
    ) as unknown) as T
  }

  return Object.keys(obj).reduce((result, key) => {
    const camelKey = snakeToCamelCase(key)
    const value = obj[key]
    result[camelKey] =
      typeof value === 'object' && value !== null
        ? objectToCamelCase(value as Record<string, unknown>)
        : value
    return result
  }, {} as Record<string, unknown>) as T
}

/**
 * Converts an object's keys from camelCase to snake_case, recursively.
 *
 * @param obj - The object to convert
 * @returns A new object with all keys in snake_case
 */
export const objectToSnakeCase = (
  obj: Record<string, unknown> | unknown[]
): Record<string, unknown> | unknown[] => {
  if (obj === null || typeof obj !== 'object') {
    return obj
  }

  if (Array.isArray(obj)) {
    return obj.map((item) =>
      typeof item === 'object' && item !== null
        ? objectToSnakeCase(item as Record<string, unknown>)
        : item
    )
  }

  return Object.keys(obj).reduce((result, key) => {
    const snakeKey = camelToSnakeCase(key)
    const value = obj[key]
    result[snakeKey] =
      typeof value === 'object' && value !== null
        ? objectToSnakeCase(value as Record<string, unknown>)
        : value
    return result
  }, {} as Record<string, unknown>)
}
